import React from 'react';
import { Link } from 'react-router-dom';
import { createPageUrl } from '../../utils';
import { menuItems } from './menuConfig';
import { usePermissions } from '../../hooks/usePermissions';

interface SidebarProps {
  activeModule: string;
  currentPageName: string;
  sidebarOpen: boolean;
  mobileMenuOpen: boolean;
  onMobileMenuClose: () => void;
}

// Map page names to required permissions
const pagePermissions: Record<string, string | string[]> = {
  // HRIS permissions
  EmployeeList: 'hris_emp.employee.view',
  OrganizationStructure: 'hris_org.organization.view',
  PunishmentList: 'hris_punish.punishment.view',
  EmployeeDevelopmentList: 'hris_dev.development.view',
  RecruitmentList: 'hris_recruit.recruitment.view',
  AttendanceList: 'hris_att.attendance.view',
  LeaveList: 'hris_leave.leave.view',
  KPIList: 'hris_kpi.kpi.view',
  PayrollList: 'hris_payroll.payroll.view',
  RoleAccessManagement: 'hris_role_access.role_access.view',

  // Credit permissions
  CreditSubmissionForm: ['credit.submission.create', 'credit.submission.update'],
  CreditApplicationList: 'credit.application.view',
  ScoringModelList: 'credit.scoring.view',
  ApprovalList: 'credit.approval.view',

  // Debtor permissions
  DebtorList: 'debtor.debtor.view',

  // Archive permissions
  ArchiveCategoryList: 'archive.category.view',
  DocumentTypeList: 'archive.general.view',
  DocumentList: 'archive.general.view',
  DocumentValidationWorkflow: 'archive.search.view',
};

export default function Sidebar({
  activeModule,
  currentPageName,
  sidebarOpen,
  mobileMenuOpen,
  onMobileMenuClose,
}: SidebarProps) {
  const { hasPermission, hasAnyPermission, isAdmin } = usePermissions();

  // Filter menu items based on permissions
  const getFilteredMenuItems = () => {
    if (!menuItems[activeModule]) return [];

    return menuItems[activeModule].filter(item => {
      // Admin users can see all menu items
      if (isAdmin) return true;

      // Dashboard pages are always visible
      if (item.page.includes('Dashboard')) return true;

      // Check if page requires permissions
      const requiredPermissions = pagePermissions[item.page];
      if (!requiredPermissions) return true; // No permission required, show item

      // Check permissions
      if (Array.isArray(requiredPermissions)) {
        return hasAnyPermission(requiredPermissions);
      } else {
        return hasPermission(requiredPermissions);
      }
    });
  };

  const filteredMenuItems = getFilteredMenuItems();

  return (
    <aside
      className={`fixed top-16 left-0 bottom-0 bg-white border-r border-gray-200 transition-all duration-300 z-40 
        ${sidebarOpen ? 'w-64' : 'w-0 -translate-x-full md:w-16 md:translate-x-0'}
        ${mobileMenuOpen ? 'translate-x-0 w-64' : ''}`}
    >
      <nav className="p-3 space-y-1 overflow-y-auto h-full">
        {filteredMenuItems.map(item => {
          // EmployeeForm should highlight EmployeeList
          // ScoringModelForm should highlight ScoringModelList
          // DebtorForm and DebtorDetail should highlight DebtorList
          // CreditSubmissionDetail should highlight ApprovalList (when accessed from approval)
          // CreditSubmissionDetail should highlight CreditApplicationList (when accessed from application list)
          // CreditSubmissionForm should also highlight CreditApplicationList (both are related to applications)
          const isActive =
            currentPageName === item.page ||
            (currentPageName === 'EmployeeForm' && item.page === 'EmployeeList') ||
            (currentPageName === 'EmployeeDetail' && item.page === 'EmployeeList') ||
            (currentPageName === 'ScoringModelForm' && item.page === 'ScoringModelList') ||
            (currentPageName === 'DebtorForm' && item.page === 'DebtorList') ||
            (currentPageName === 'DebtorDetail' && item.page === 'DebtorList') ||
            (currentPageName === 'CreditSubmissionDetail' && item.page === 'ApprovalList') ||
            (currentPageName === 'CreditApplicationList' && item.page === 'CreditApplicationList');
          return (
            <Link
              key={item.page}
              to={createPageUrl(item.page)}
              onClick={onMobileMenuClose}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
                ${isActive ? 'bg-[#1e3a5f] text-white' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              <span
                className={`${!sidebarOpen && !mobileMenuOpen ? 'hidden' : ''} whitespace-nowrap`}
              >
                {item.name}
              </span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
